import { FC, useContext, useEffect, useMemo, useState } from "react";
import { TaskContext } from "../../../contexts/TaskContext";
import { AppContext } from "../../../App";
import { SEO } from "../../Helmet/HelmetSEO";
import TaskInformationForm from "../TaskForm/TaskInformationForm";
import { CurrentTab, TabNavigator } from "../../UiLib/TabNavigator/TabNavigator";
import InputChat from "../../Shared/InputChat/InputChat";
import SubTasksGraph from "../TaskRightInfo/SubTasksGraph/SubTasksGraph";
import TaskHistory from "../TaskHistory/TaskHistory";
import TaskRightInfo from "../TaskRightInfo/TaskRightInfo";
import { HISTORY_PAGE_SIZE } from "../../../constants/TaskConst";
import TaskChecklist from "../TaskChecklist/TaskChecklist";
import { hasPermission } from "../../../helpers/hasPermisson";
import TaskIntegrationTab from "../TaskIntegrationTab/TaskIntegrationTab";
import ProjectService from "../../../services/ProjectService";
import { isAxiosError } from "axios";
import { systemFunctions } from "../../../common/enums/systemFunctions";
import { accessPermission } from "../../../common/enums/accessPermission";

const TaskMain: FC = () => {
    const taskContext = useContext(TaskContext);
    const { pageTitle, showToast, systemUserAccess } = useContext(AppContext);
    const [showExternalTasks, setShowExternalTasks] = useState<boolean>(false);

    const viewCheckList = useMemo(
        () =>
            hasPermission(systemUserAccess, "ChecklistAction", "view") ||
            hasPermission(taskContext.userAccess, "ChecklistAction", "view"),
        [systemUserAccess, taskContext.userAccess]
    );

    const viewIntegration = useMemo(
        () =>
            hasPermission(systemUserAccess, systemFunctions.IntegrationSystemAction, accessPermission.view) ||
            hasPermission(taskContext.userAccess, systemFunctions.IntegrationSystemAction, accessPermission.view),
        [systemUserAccess, taskContext.userAccess]
    );

    const editIntegration = useMemo(
        () =>
            hasPermission(systemUserAccess, systemFunctions.IntegrationSystemAction, accessPermission.edit) ||
            hasPermission(taskContext.userAccess, systemFunctions.IntegrationSystemAction, accessPermission.edit),
        [systemUserAccess, taskContext.userAccess]
    );

    const seoTitle = useMemo(() => {
        let taskInformation = taskContext.taskInformation;

        if (!taskInformation) return pageTitle;

        const shortDescription =
            taskInformation.shortDescription.length > 70
                ? `${taskInformation.shortDescription.slice(0, 67)}...`
                : taskInformation.shortDescription;

        return `${taskInformation.idForView} · ${shortDescription} | Задача · ${pageTitle}`;
    }, [taskContext.taskInformation, pageTitle]);

    useEffect(() => {
        (async () => {
            try {
                if (!taskContext.taskInformation) return;
                const project = await ProjectService.getProjectGeneralInfo(taskContext.taskInformation.projectId);
                if (project.data.externalPlan) setShowExternalTasks(true);
            } catch (err) {
                if (isAxiosError(err)) showToast(err.response?.data.message);
            }
        })();
    }, [taskContext.taskInformation, showToast]);

    const tabs = useMemo(
        () =>
            taskContext.taskInformation
                ? [
                      {
                          name: "Комментарии",
                          render: (
                              <InputChat
                                  userAccess={taskContext.userAccess}
                                  chatId={taskContext.taskInformation.chatId}
                                  projectId={taskContext.taskInformation.projectId}
                                  messageUrlQueryParams="tab=0"
                                  optionsQueryParams={{
                                      page: "task",
                                      entity: "chat",
                                      action: undefined,
                                      entityId: undefined,
                                  }}
                                  showTaskActions
                                  taskId={taskContext.taskInformation.id}
                                  boardId={taskContext.currentPageBoardId}
                                  onActionsExecuted={taskContext.loadTaskInfo}
                              />
                          ),
                      },
                      viewCheckList
                          ? {
                                name: "Чек-лист",
                                render: (
                                    <TaskChecklist
                                        openAllDropdown={taskContext.checkList === 1}
                                        systemUserAccess={systemUserAccess}
                                        userAccess={taskContext.userAccess}
                                        checklistGroups={taskContext.checklistGroups}
                                        loadChecklist={taskContext.loadChecklist}
                                        optionsQueryParams={{
                                            page: "task",
                                            entity: undefined,
                                            action: undefined,
                                            entityId: undefined,
                                        }}
                                    />
                                ),
                            }
                          : null,
                      {
                          name: "Связанные задачи",
                          render: (
                              <div className="widget_wrapper" style={{ margin: "10px" }}>
                                  {taskContext.taskInformation?.id && (
                                      <SubTasksGraph
                                          taskId={taskContext.taskInformation?.id}
                                          treeHead={taskContext.subtasksHead}
                                      />
                                  )}
                              </div>
                          ),
                      },
                      {
                          name: "История по задаче",
                          render: (
                              <div id="main-history-widget" style={{ margin: "10px" }} className="widget_wrapper">
                                  <div className="task-history-table-container">
                                      {taskContext.viewHistory && taskContext.taskInformation && (
                                          <TaskHistory
                                              records={taskContext.taskHistory}
                                              onChangeFilters={(filters) =>
                                                  taskContext.loadTaskHistory(0, HISTORY_PAGE_SIZE, filters, false)
                                              }
                                              projectId={taskContext.taskInformation.projectId}
                                          />
                                      )}
                                  </div>
                              </div>
                          ),
                      },
                      showExternalTasks && viewIntegration
                          ? {
                                name: "Внешняя задача",
                                render: (
                                    <div id="main-history-widget" style={{ margin: "10px" }} className="widget_wrapper">
                                        <TaskIntegrationTab canEdit={editIntegration}/>
                                    </div>
                                ),
                            }
                          : null,
                  ].filter(Boolean)
                : undefined,
        [taskContext, showExternalTasks]
    );

    return (
        <>
            <SEO title={seoTitle} />

            <div className="widgets_task_container">
                <div
                    id="task-chat-wrapper-scrollable-part"
                    className="widgets_task_container_1"
                    onScroll={taskContext.onScroll}
                    ref={taskContext.scrollComponentDiv}
                >
                    <div className="widgets_task_container_block">
                        <div className="widgets_task_container_sub">
                            <TaskInformationForm
                                taskNumber={taskContext.taskInformation?.idForView || ""}
                                projectId={taskContext.taskInformation?.projectId}
                                userAccess={taskContext.userAccess}
                                optionsQueryParams={{
                                    page: "task",
                                    entity: undefined,
                                    action: undefined,
                                    entityId: undefined,
                                }}
                            />
                        </div>

                        <TabNavigator defaultTab={taskContext.tab} keepMounted={true} classBlock="task-tabs">
                            {tabs?.map((tab, index) => (
                                <CurrentTab name={tab?.name ?? ""} callBack={() => taskContext.changeTab(index)}>
                                    {tab?.render}
                                </CurrentTab>
                            ))}
                        </TabNavigator>
                    </div>
                </div>

                <div className="widgets_task_container_2">
                    <TaskRightInfo
                        setSubTasks={taskContext.setSubTasks}
                        userAccess={taskContext.userAccess}
                        optionsQueryParams={{
                            page: "task",
                            entity: undefined,
                            action: undefined,
                            entityId: undefined,
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default TaskMain;
